import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Public NDA signing page (owner-facing, German, light "paper" look)
export default function PublicNdaPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [fields, setFields] = useState({});
  const [signatureName, setSignatureName] = useState('');
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedNow, setSignedNow] = useState(false);
  const [signError, setSignError] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/api/public/nda/${token}`, { withCredentials: false })
      .then(r => { setData(r.data); setFields(r.data.fields || {}); })
      .catch(() => setError('Diese Vereinbarung wurde nicht gefunden oder ist nicht mehr gültig.'));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const sign = async () => {
    setSignError('');
    if (!agree) { setSignError('Bitte bestätigen Sie die Zustimmung zur elektronischen Signatur.'); return; }
    if (signatureName.trim().length < 3) { setSignError('Bitte geben Sie Ihren vollständigen Namen als Unterschrift ein.'); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/api/public/nda/${token}/sign`,
        { fields, signature_name: signatureName.trim(), agree: true }, { withCredentials: false });
      setSignedNow(true);
      load();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSignError(err.response?.data?.detail || 'Die Signatur konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
    }
    setSigning(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-6">
        <p className="text-stone-500 text-sm" data-testid="nda-public-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-stone-100 flex items-center justify-center"><div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const isSigned = data.status === 'signed';
  const tpl = data.template;
  const label = (k) => tpl.fields.find(f => f.key === k)?.label || k;

  // Rendered via function calls (not <Component/>) so inputs never remount → focus is kept while typing
  const FieldInline = ({ k, width = 'w-56' }) => {
    if (isSigned || (data.fields[k] || '').trim()) {
      return <span className="font-semibold">{fields[k] || data.fields[k] || '—'}</span>;
    }
    return (
      <input value={fields[k] || ''} onChange={e => setField(k, e.target.value)} placeholder={label(k)}
        data-testid={`public-nda-field-${k}`}
        className={`${width} inline-block bg-amber-100 border-b-2 border-amber-400 focus:border-teal-600 outline-none px-1.5 py-0.5 rounded-t text-stone-900 placeholder-amber-700/60 text-sm`} />
    );
  };

  return (
    <div className="min-h-screen bg-stone-200 py-6 px-3 sm:px-6" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="max-w-3xl mx-auto">
        {(isSigned || signedNow) && (
          <div className="bg-emerald-600 text-white rounded-md px-5 py-4 mb-4 shadow-lg" data-testid="nda-signed-banner">
            <p className="font-bold text-base">✓ Vereinbarung unterzeichnet</p>
            <p className="text-sm mt-1 text-emerald-50">
              Vielen Dank{data.signature_name ? `, ${data.signature_name}` : ''}. Sie erhalten die von beiden Parteien
              unterzeichnete Ausfertigung automatisch per E-Mail.
            </p>
            <a href={`${API}/api/public/nda/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="nda-download-pdf"
              className="inline-block mt-2 text-sm bg-white/15 hover:bg-white/25 rounded px-3 py-1.5 transition-colors">
              PDF herunterladen / drucken
            </a>
          </div>
        )}

        {!isSigned && (
          <div className="bg-teal-700 text-white rounded-md px-5 py-3 mb-4 shadow" data-testid="nda-intro-banner">
            <p className="text-sm">
              Sehr geehrte(r) {data.recipient_name}, bitte prüfen Sie die Vereinbarung, ergänzen Sie die
              <span className="bg-amber-300 text-stone-900 px-1 mx-1 rounded font-semibold">gelb markierten Felder</span>
              und unterzeichnen Sie unten elektronisch. OLTA hat bereits gezeichnet.
            </p>
          </div>
        )}

        <div className="bg-white shadow-xl rounded-sm px-6 sm:px-12 py-10 text-stone-900 leading-relaxed">
          <h1 className="text-center text-xl font-bold">{tpl.title}</h1>
          <p className="text-center text-stone-500 text-sm mb-6">{tpl.subtitle}</p>
          <p className="text-center text-sm">zwischen</p>
          <div className="text-center my-3 text-sm">
            <p className="font-bold">{tpl.olta_block[0]}</p>
            <p>{tpl.olta_block[1]}</p>
            <p>{tpl.olta_block[2]}</p>
            <p className="text-stone-500 mt-1">– nachfolgend „OLTA“ –</p>
          </div>
          <p className="text-center text-sm">und</p>
          <div className="text-center my-3 text-sm space-y-1.5">
            <p className="font-bold">{FieldInline({ k: 'company_name', width: 'w-72' })}</p>
            <p>{FieldInline({ k: 'street', width: 'w-56' })}, {FieldInline({ k: 'zip_city', width: 'w-44' })}</p>
            {(!isSigned || (data.fields.register_court || '').trim() || (data.fields.register_no || '').trim()) && (
              <p className="text-stone-600">
                eingetragen im Handelsregister des Amtsgerichts {FieldInline({ k: 'register_court', width: 'w-36' })} unter
                HRB {FieldInline({ k: 'register_no', width: 'w-28' })}
                {!isSigned && <span className="block text-[11px] text-stone-400 mt-0.5">(optional — leer lassen, falls nicht eingetragen)</span>}
              </p>
            )}
            <p className="text-stone-500">– nachfolgend „{tpl.party_label || 'Unternehmen'}“ –</p>
          </div>
          <p className="text-center text-xs text-stone-500 mb-6">
            – OLTA und {tpl.party_line || 'das Unternehmen'} nachfolgend einzeln auch „Partei“ und gemeinsam „Parteien“ –
          </p>

          <h2 className="font-bold text-sm mt-5 mb-1">Präambel</h2>
          <p className="text-sm text-justify">{tpl.preamble}</p>
          {tpl.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-bold text-sm mt-5 mb-1">{s.heading}</h2>
              {s.paragraphs.map((p, j) => <p key={j} className="text-sm text-justify mb-1.5">{p}</p>)}
            </div>
          ))}

          {/* Signatures */}
          <div className="grid sm:grid-cols-2 gap-8 mt-10 pt-6 border-t border-stone-300">
            <div className="text-sm">
              <p className="text-stone-600">Ort, Datum: {data.olta.place}, {data.olta.date}</p>
              <p className="font-bold mt-3">OLTA Nachfolge GmbH</p>
              <p className="mt-5 text-3xl" style={{ fontFamily: "'Great Vibes', cursive" }}>{data.olta.signer_name}</p>
              <div className="border-t border-stone-400 mt-1 pt-1">
                <p>Name: {data.olta.signer_name}</p>
                <p>Funktion: {data.olta.function}</p>
                <p className="text-[11px] text-emerald-700 mt-1">✓ elektronisch signiert</p>
              </div>
            </div>
            <div className="text-sm" data-testid="nda-owner-signature-block">
              <p className="text-stone-600">
                Ort: {FieldInline({ k: 'signer_place', width: 'w-36' })}, Datum: {new Date().toLocaleDateString('de-DE')}
              </p>
              <p className="font-bold mt-3">{fields.company_name || data.fields.company_name || 'Unternehmen'}</p>
              {isSigned ? (
                <>
                  <p className="mt-5 text-3xl" style={{ fontFamily: "'Great Vibes', cursive" }}>{data.signature_name}</p>
                  <div className="border-t border-stone-400 mt-1 pt-1">
                    <p>Name: {data.fields.signer_name}</p>
                    <p>Funktion: {data.fields.signer_function}</p>
                    <p className="text-[11px] text-emerald-700 mt-1">✓ elektronisch signiert am {data.signed_at ? new Date(data.signed_at).toLocaleString('de-DE') : ''}</p>
                  </div>
                </>
              ) : (
                <div className="mt-3 space-y-2">
                  <p>Name: {FieldInline({ k: 'signer_name', width: 'w-52' })}</p>
                  <p>Funktion: {FieldInline({ k: 'signer_function', width: 'w-52' })}</p>
                </div>
              )}
            </div>
          </div>

          {!isSigned && (
            <div className="mt-10 bg-teal-50 border-2 border-teal-600 rounded-md p-5" data-testid="nda-sign-box">
              <p className="font-bold text-teal-900 text-sm mb-3">Elektronische Unterschrift</p>
              <label className="block text-xs text-stone-600 mb-1">
                Bitte tippen Sie Ihren vollständigen Namen — er gilt als Ihre Unterschrift:
              </label>
              <input value={signatureName} onChange={e => setSignatureName(e.target.value)}
                placeholder="Vor- und Nachname" data-testid="nda-signature-input"
                className="w-full sm:w-96 border-2 border-stone-300 focus:border-teal-600 outline-none rounded px-3 py-2 text-2xl bg-white"
                style={{ fontFamily: "'Great Vibes', cursive" }} />
              <label className="flex items-start gap-2 mt-4 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
                  data-testid="nda-agree-checkbox" className="mt-0.5 w-4 h-4 accent-teal-600 cursor-pointer" />
                <span className="text-xs text-stone-700">
                  Ich bin zur Unterzeichnung für {tpl.variant === 'broker' ? 'die oben genannte Beratungsgesellschaft' : 'das oben genannte Unternehmen'} berechtigt und stimme zu, dass meine
                  getippte Unterschrift zusammen mit Zeitstempel und Protokollierung als verbindliche elektronische
                  Signatur in Textform (§ 126b BGB) gilt.
                </span>
              </label>
              {signError && <p className="text-red-600 text-xs mt-2" data-testid="nda-sign-error">{signError}</p>}
              <button onClick={sign} disabled={signing} data-testid="nda-sign-btn"
                className="mt-4 bg-teal-700 hover:bg-teal-600 disabled:opacity-60 text-white font-bold rounded-md px-8 py-3 text-sm transition-colors">
                {signing ? 'Wird signiert…' : 'Jetzt verbindlich unterzeichnen'}
              </button>
              <p className="text-[11px] text-stone-500 mt-2">
                Nach der Unterzeichnung erhalten Sie die vollständig unterzeichnete Ausfertigung als PDF per E-Mail.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4 text-xs text-stone-500">
          <span>OLTA Nachfolge GmbH · Annastraße 58-60, 52062 Aachen</span>
          <a href={`${API}/api/public/nda/${token}/pdf`} target="_blank" rel="noreferrer"
            className="underline hover:text-stone-700" data-testid="nda-print-link">Als PDF drucken</a>
        </div>
      </div>
    </div>
  );
}
